import { useEffect, useState } from 'react';

import { useGetCohortAttritionByCohortProfileIdQuery } from '@verantos-dev/data-access-cohort-attrition';
import { useGetCohortSummaryByCohortProfileIdQuery } from '@verantos-dev/data-access-cohort-summary';
import { useGetSubjectsByProfileIdQuery } from '@verantos-dev/data-access-subjects';
import { CohortDefinitionStatus } from '@verantos-dev/types-cohorts';

/**
 * Tracks whether to start fetching data
 * Delay 3 seconds to avoid fetching data alongside more important cohort data
 * @returns {boolean} startDelayedFetch - whether to start fetching data
 */
const useDelayedInitialFetch = () => {
  const [startDelayedFetch, setStartDelayedFetch] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setStartDelayedFetch(true);
    }, 3000);
  }, []);

  return startDelayedFetch;
};

/**
 * Pre-fetches cohort results data
 * @param projectId - project id
 * @param cohortId - cohort id
 * @param cohortStatus - cohort status
 * @returns {object} - object indicating whether a delayed final fetch timer is active
 */
export const usePrefetchResultsData = (
  projectId: string | undefined,
  cohortId: string | undefined,
  cohortStatus: CohortDefinitionStatus | undefined
) => {
  // Delay 3 seconds to avoid fetching data alongside more important cohort data, on initial load
  const startDelayedFetch = useDelayedInitialFetch();

  // tracks whether to fetch final results
  const [finalFetch, setFinalFetch] = useState(false);
  const [isFinalFetchTimerActive, setIsFinalFetchTimerActive] = useState(false);

  /** pre-fetch cohort attrition data */
  const attritionQuery = useGetCohortAttritionByCohortProfileIdQuery(
    {
      cohortProfileId: cohortId || '',
      projectId: projectId || '',
    },
    {
      skip: !projectId || !cohortId || !startDelayedFetch || finalFetch,
      pollingInterval: cohortStatus === CohortDefinitionStatus.PROCESSING ? 5000 : undefined,
      refetchOnMountOrArgChange: finalFetch,
    }
  );

  /** pre-fetch cohort summary data */
  const summaryQuery = useGetCohortSummaryByCohortProfileIdQuery(
    {
      projectId: projectId || '',
      cohortProfileId: cohortId || '',
    },
    {
      skip: !projectId || !cohortId || !startDelayedFetch || finalFetch,
      pollingInterval: cohortStatus === CohortDefinitionStatus.PROCESSING ? 5000 : undefined,
      refetchOnMountOrArgChange: finalFetch,
    }
  );

  /** pre-fetch subjects list */
  const subjectsQuery = useGetSubjectsByProfileIdQuery(
    {
      projectId: projectId || '',
      cohortProfileId: cohortId || '',
      urlParams: '',
    },
    {
      skip: !projectId || !cohortId || !startDelayedFetch || finalFetch,
      pollingInterval: cohortStatus === CohortDefinitionStatus.PROCESSING ? 5000 : undefined,
      refetchOnMountOrArgChange: finalFetch,
    }
  );

  // Perform one additional fetch when the cohort status changes to "PROCESSED"
  // This is needed because above we are polling for the latest results only when the cohort is in "PROCESSING" status,
  // but once the status changes to "PROCESSED" we stop polling for the latest results
  useFinalResultsFetchWhenCohortChangesToProcessed(
    finalFetch,
    setFinalFetch,
    setIsFinalFetchTimerActive,
    cohortStatus,
    attritionQuery,
    summaryQuery,
    subjectsQuery
  );

  return {
    isFinalFetchTimerActive,
  };
};

const useFinalResultsFetchWhenCohortChangesToProcessed = (
  finalFetch: boolean,
  setFinalFetch: (value: boolean) => void,
  setIsFinalFetchTimerActive: (value: boolean) => void,
  cohortStatus: CohortDefinitionStatus | undefined,
  attritionQuery: ReturnType<typeof useGetCohortAttritionByCohortProfileIdQuery>,
  summaryQuery: ReturnType<typeof useGetCohortSummaryByCohortProfileIdQuery>,
  subjectsQuery: ReturnType<typeof useGetSubjectsByProfileIdQuery>
) => {
  // reset final fetch when cohort status changes
  useEffect(() => {
    if (cohortStatus === CohortDefinitionStatus.PROCESSED) {
      setIsFinalFetchTimerActive(true);
      const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
      const fetchFinalResults = async () => {
        await sleep(5000);
        setFinalFetch(true);
      };
      fetchFinalResults();
    }
  }, [cohortStatus, setFinalFetch, setIsFinalFetchTimerActive]);

  // refetch final results
  useEffect(() => {
    if (finalFetch) {
      if (
        !attritionQuery.data ||
        !attritionQuery.data?.data ||
        attritionQuery.data.data.length === 0
      ) {
        attritionQuery.fulfilledTimeStamp && attritionQuery.refetch();
      }
      if (!summaryQuery.data || !summaryQuery.data?.data) {
        summaryQuery.fulfilledTimeStamp && summaryQuery.refetch();
      }
      if (
        !subjectsQuery.data ||
        !subjectsQuery.data?.data ||
        subjectsQuery.data?.data.length === 0
      ) {
        subjectsQuery.fulfilledTimeStamp && subjectsQuery.refetch();
      }
      setFinalFetch(false);
      setIsFinalFetchTimerActive(false);
    }
  }, [
    finalFetch,
    attritionQuery,
    summaryQuery,
    subjectsQuery,
    setFinalFetch,
    setIsFinalFetchTimerActive,
  ]);

  return finalFetch;
};
